import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {teamGroupToLabel} from '@/features/projects/utils'
import {useTranslation, TKey} from '@/translations/i18n'
import {useController, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {useMemo} from 'react'
import {TeamGroup} from '@/features/treatment-requests/teams/types'

interface EditProjectTeamGroupFieldProps {
    index: number
    isAReserve?: boolean
}

const EditProjectTeamGroupFieldSchema = ProjectFieldsSchema.pick({
    team: true
})
type EditProjectTeamGroupFieldSchema = z.infer<typeof EditProjectTeamGroupFieldSchema>

export const EditProjectTeamGroupField = ({isAReserve, index}: EditProjectTeamGroupFieldProps) => {
    const {t} = useTranslation()
    const {control} = useFormContext<EditProjectTeamGroupFieldSchema>()
    const {field} = useController({
        control,
        name: `team.${index}`
    })

    // Watch the whole team to enforce the "only one MRP (referring_doctor)" constraint
    const team = useWatch({control, name: 'team'})

    // True if another team member (not this row) has already been assigned the referring_doctor group
    const isReferringDoctorTakenByAnotherMember = useMemo(() => {
        return (team ?? []).some((member, memberIndex) => memberIndex !== index && member.group === 'referring_doctor')
    }, [team, index])

    // Create a filtered version of the options that excludes referring_doctor when:
    // - this row is a reserve, or
    // - another team member already occupies the referring_doctor (MRP) role
    const filteredOptions = useMemo(() => {
        if (!isAReserve && !isReferringDoctorTakenByAnotherMember) {
            return teamGroupToLabel
        }

        // Filter out referring_doctor from options
        return Object.fromEntries(
            Object.entries(teamGroupToLabel).filter(([key]) => key !== 'referring_doctor')
        ) as Record<TeamGroup, TKey>
    }, [isAReserve, isReferringDoctorTakenByAnotherMember])

    const handleChangeSpecializationGroup = (value: TeamGroup) => {
        field.onChange({
            id: null,
            fullName: null,
            specializationName: null,
            group: value
        })
    }

    return (
        <InputSelect
            label={t('projects:group')}
            placeholder={t('projects:select_group')}
            optionsMap={filteredOptions}
            value={field.value.group}
            onChange={handleChangeSpecializationGroup}
        />
    )
}
